import _ from 'lodash'
import type { BaseDataItem, RefInfo } from '@wix/document-services-types'
import type { Data, RefSchemas } from '@wix/thunderbolt-becky-types'
import type { DataWithDrafts } from './dataResolvingTypes'
import { isDraftDataItem } from './dataResolvingUtils'

type FixerFunction = (refs: Array<RefInfo>) => Array<RefInfo>

const makeAllRefsNotWeak = (refs: Array<RefInfo>) => refs.map((ref) => ({ ...ref, isRefOwner: true }))
const schemaFixer: Record<string, FixerFunction> = {
	TinyMenu: makeAllRefsNotWeak,
	CustomMenuDataRef: makeAllRefsNotWeak,
	BreadcrumbsData: makeAllRefsNotWeak,
	MenuData: makeAllRefsNotWeak,
}

export const createRefInfo = (refInfo: Partial<RefInfo>): RefInfo => ({
	jsonPointer: '',
	refTypes: [],
	shouldCollect: false,
	shouldValidate: false,
	isRelationalSplit: false,
	isList: false,
	referencedMap: '',
	path: [],
	...refInfo,
})

const EMPTY_ARRAY: Array<RefInfo> = []

export const createSchemaManager = (schemas: RefSchemas) => {
	const staticRefsCache: RefSchemas = {}
	const dynamicRefsCache: RefSchemas = {}

	// Remove cross maps refs because in CARMI we can't resolve them (while in catharsis we do resolve them)
	const getSchemaByType = (
		mapName: keyof Data,
		dataType: string,
		schemasToUse = schemas,
		cache = staticRefsCache
	) => {
		if (!cache[mapName]?.[dataType]) {
			cache[mapName] = cache[mapName] ?? {}
			const refs = schemasToUse[`${mapName}.json`]?.[dataType] || schemasToUse[`${mapName}`]?.[dataType]
			if (!refs) {
				cache[mapName][dataType] = EMPTY_ARRAY
				return cache[mapName][dataType]
			}

			const fixedRefs = schemaFixer[dataType]?.(refs) ?? refs
			cache[mapName][dataType] = fixedRefs.filter((ref) => mapName === ref.referencedMap)
		}

		return cache[mapName][dataType]
	}

	const getSchemaFromDataItem = (
		mapName: keyof Data,
		dataItem: DataWithDrafts | BaseDataItem,
		dynamicRefs?: RefSchemas,
	) => {
		if (!dataItem) {
			return undefined
		}

		if (isDraftDataItem(dataItem) && dataItem._draftRefs) {
			return dataItem._draftRefs.filter(({ referencedMap }) => !referencedMap || referencedMap === mapName)
		}

		if (dynamicRefs) {
			const dynamicSchema = getSchemaByType(mapName, dataItem.type, dynamicRefs, dynamicRefsCache)
			if (dynamicSchema && dynamicSchema.length) {
				return dynamicSchema
			}
		}

		return getSchemaByType(mapName, dataItem.type)
	}

	return { getSchemaFromDataItem, getSchemaByType }
}
